import { useEffect, useMemo, useState } from 'react'
import { AnalyticsScreenConstants } from '@buxus/shared'
import { AppLoadingRow } from '../components/AppBootLoading'
import { InlineSpinner } from '../components/InlineSpinner'
import { NavMdiIcon } from '../NavTabIcons'
import { trackRichHtmlLinkClick } from '../platform/analytics/interactionAnalytics'
import {
  trackWebFileOpenFailed,
  trackWebFileOpenPressed,
  trackWebFileOpenStarted,
  trackWebFileOpenSucceeded,
  webFileOpenFailurePhaseFromError,
} from '../platform/analytics/webFileOpenAnalytics'
import { fetchSasBlob } from '../platform/buxus/operatorApi'
import { isOperatorFileViewableInTab, presentOperatorFileBlob } from '../platform/buxus/operatorFileOpen'
import { showAppErrorModal } from '../platform/ui/appErrorModal'
import { mdiOpenInNew } from '@mdi/js'
import { TrainingDocumentIcon } from './TrainingDocumentIcon'
import {
  formatTrainingFileBytes,
  formatTrainingListDateFromDoc,
  getTrainingFileExtension,
  isTrainingBlobPdfExtension,
  isTrainingInlineTextOrHtmlBlob,
  normalizeTrainingFeature,
  TRAINING_FEATURE_SCENARIO,
  TRAINING_FEATURE_TOOLBOX_TIP,
  trainingDocumentKey,
} from './trainingDocument'
import {
  TRAINING_SCENARIO_DETAIL_SCREEN,
  TRAINING_TOOLBOX_TIP_DETAIL_SCREEN,
} from './trainingScreens'
import { useTrainingDocuments } from './useTrainingDocuments'

type TrainingDocumentDetailScreenProps = {
  screenId: typeof TRAINING_SCENARIO_DETAIL_SCREEN | typeof TRAINING_TOOLBOX_TIP_DETAIL_SCREEN
  trainingDocId: string | undefined
  primaryHex: string
}

function analyticsScreenLabel(screenId: string): string {
  return screenId === TRAINING_TOOLBOX_TIP_DETAIL_SCREEN
    ? AnalyticsScreenConstants.TOOLBOX_TIP_DETAIL_SCREEN
    : AnalyticsScreenConstants.TRAINING_SCENARIO_DETAIL_SCREEN
}

export function TrainingDocumentDetailScreen({
  screenId,
  trainingDocId,
  primaryHex,
}: TrainingDocumentDetailScreenProps) {
  const { documents, loading, error } = useTrainingDocuments()
  const [opening, setOpening] = useState(false)
  const [inlineText, setInlineText] = useState<string | null>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [inlineLoading, setInlineLoading] = useState(false)

  const expectedFeature =
    screenId === TRAINING_TOOLBOX_TIP_DETAIL_SCREEN
      ? TRAINING_FEATURE_TOOLBOX_TIP
      : TRAINING_FEATURE_SCENARIO

  const doc = useMemo(() => {
    if (!trainingDocId) return null
    return (
      documents.find((d) => {
        if (normalizeTrainingFeature(d) !== expectedFeature) return false
        return trainingDocumentKey(d) === trainingDocId || d.fileName === trainingDocId
      }) ?? null
    )
  }, [documents, expectedFeature, trainingDocId])

  const fetchesInline = !!doc && isTrainingInlineTextOrHtmlBlob(doc)
  const ext = doc ? getTrainingFileExtension(doc.fileName) : ''

  useEffect(() => {
    if (!doc || !fetchesInline) {
      setInlineText(null)
      setInlineError(null)
      setInlineLoading(false)
      return
    }
    let cancelled = false
    setInlineLoading(true)
    setInlineError(null)
    setInlineText(null)
    void (async () => {
      try {
        const blob = await fetchSasBlob(doc.sasUrl)
        const text = await blob.text()
        if (!cancelled) setInlineText(text)
      } catch (err) {
        if (!cancelled) {
          setInlineError(err instanceof Error ? err.message : 'Unable to load this document.')
        }
      } finally {
        if (!cancelled) setInlineLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [doc, fetchesInline])

  const screenLabel = analyticsScreenLabel(screenId)

  const openPdf = async () => {
    if (!doc) return
    const fileExt = getTrainingFileExtension(doc.fileName)
    const fileOpenBase = {
      screen: screenLabel,
      fileId: doc.id,
      fileName: doc.fileName,
      fileExt,
      title: doc.title,
    }
    const extra = {
      trainingFeature: normalizeTrainingFeature(doc),
      ...(doc.categoryName?.trim() ? { categoryName: doc.categoryName.trim() } : {}),
      sizeBytes: doc.sizeBytes,
    }
    trackWebFileOpenPressed(fileOpenBase, extra)

    const viewInTab = isTrainingBlobPdfExtension(doc.fileName) || isOperatorFileViewableInTab(fileExt)
    const viewTab = viewInTab ? window.open('about:blank', '_blank') : null
    if (viewInTab && !viewTab) {
      trackWebFileOpenFailed(
        fileOpenBase,
        { errorPhase: 'popup_blocked', errorMessage: 'popup_blocked' },
        extra,
      )
      showAppErrorModal({
        message: 'Could not open a new tab. Allow pop-ups for this site and try again.',
      })
      return
    }

    trackWebFileOpenStarted(fileOpenBase, extra)
    setOpening(true)
    try {
      const blob = await fetchSasBlob(doc.sasUrl)
      await presentOperatorFileBlob(blob, {
        fileId: doc.id,
        fileExt,
        fileName: doc.fileName,
        displayName: doc.title,
        viewTab,
      })
      trackWebFileOpenSucceeded(fileOpenBase, extra)
    } catch (e) {
      if (viewTab && !viewTab.closed) viewTab.close()
      const msg = e instanceof Error ? e.message : String(e)
      trackWebFileOpenFailed(
        fileOpenBase,
        { errorPhase: webFileOpenFailurePhaseFromError(msg), errorMessage: msg },
        extra,
      )
      showAppErrorModal({
        message:
          msg.includes('pop-up') || msg.includes('pop-ups')
            ? msg
            : 'This file is unavailable. Try again, or contact support if it continues.',
      })
    } finally {
      setOpening(false)
    }
  }

  if (loading) {
    return (
      <section className="app-download-card training-docs-card">
        <AppLoadingRow label="Loading…" />
      </section>
    )
  }

  if (error) {
    return (
      <section className="app-download-card training-docs-card">
        <p className="app-download-error">{error}</p>
      </section>
    )
  }

  if (!doc) {
    return (
      <section className="app-download-card training-docs-card">
        <p className="app-download-empty">
          This training document is no longer available. Please go back and try again.
        </p>
      </section>
    )
  }

  const toolboxWithInlineBody =
    normalizeTrainingFeature(doc) === TRAINING_FEATURE_TOOLBOX_TIP && fetchesInline
  const isToolboxTip = normalizeTrainingFeature(doc) === TRAINING_FEATURE_TOOLBOX_TIP
  const toolboxDescriptionFromMetadata =
    isToolboxTip && !fetchesInline ? doc.description?.trim() ?? '' : ''
  const showStructuredSubsectionHeading =
    !toolboxWithInlineBody &&
    !!(doc.objective?.trim() || doc.jprsMet?.trim() || doc.resourceMaterial?.trim())
  const showSlugLine = !!(doc.fileName && !toolboxWithInlineBody)
  const detailDateLine = formatTrainingListDateFromDoc(doc)
  const showOpen = isTrainingBlobPdfExtension(doc.fileName)
  const sizeLabel = formatTrainingFileBytes(doc.sizeBytes)
  const inlineSettled = !fetchesInline || inlineText !== null || !!inlineError
  const interactionContext = { screen: screenLabel }

  return (
    <section className="app-download-card training-docs-card">
      <div className="training-detail-title-row">
        <TrainingDocumentIcon document={doc} size="detail" />
        <div className="training-detail-title-text">
          <h2 className="app-download-title training-detail-title">{doc.title}</h2>
          {detailDateLine ? <p className="training-detail-meta">{detailDateLine}</p> : null}
          {showSlugLine ? (
            <p className="training-detail-meta">
              {[doc.fileName, sizeLabel].filter(Boolean).join(' · ')}
            </p>
          ) : null}
        </div>
      </div>

      {!toolboxWithInlineBody && doc.objective ? (
        <DetailSection label="Objective" value={doc.objective} />
      ) : null}
      {!toolboxWithInlineBody && doc.jprsMet ? (
        <DetailSection label="JPRs Met" value={doc.jprsMet} />
      ) : null}
      {!toolboxWithInlineBody && doc.resourceMaterial ? (
        <DetailSection label="Resource Material" value={doc.resourceMaterial} />
      ) : null}

      {fetchesInline && inlineLoading ? (
        <div className="training-detail-inline-status">
          <InlineSpinner label="Loading document" />
        </div>
      ) : null}
      {fetchesInline && inlineError ? <p className="app-download-error">{inlineError}</p> : null}

      {toolboxDescriptionFromMetadata ? (
        <DetailSection label="Description" value={toolboxDescriptionFromMetadata} />
      ) : null}

      {fetchesInline && ext === 'txt' && inlineText !== null ? (
        showStructuredSubsectionHeading ? (
          <DetailSection label="Content" value={inlineText} />
        ) : (
          <div className="training-detail-section">
            <div className="training-detail-top-rule" />
            <p className="training-detail-value">{inlineText}</p>
          </div>
        )
      ) : null}

      {fetchesInline && (ext === 'html' || ext === 'htm') && inlineText !== null ? (
        <div className="training-detail-section">
          {showStructuredSubsectionHeading ? (
            <div className="training-detail-heading">Content</div>
          ) : (
            <div className="training-detail-top-rule" />
          )}
          <div
            className="rich training-detail-html"
            dangerouslySetInnerHTML={{ __html: inlineText }}
            onClick={(e) => trackRichHtmlLinkClick(e, interactionContext)}
          />
        </div>
      ) : null}

      {doc.imageUrl?.trim() && inlineSettled ? (
        <div className="training-detail-section">
          <div className="training-detail-heading">Image</div>
          <img
            className="training-detail-hero"
            src={doc.imageUrl.trim()}
            alt={doc.title ? `Image · ${doc.title}` : 'Document image'}
          />
        </div>
      ) : null}

      {showOpen ? (
        <button
          type="button"
          className="app-training-cta app-training-cta-with-icon app-download-btn primary"
          style={{ backgroundColor: primaryHex, borderColor: primaryHex }}
          onClick={() => void openPdf()}
          disabled={opening}
        >
          {opening ? (
            <InlineSpinner label="Opening file" />
          ) : (
            <NavMdiIcon path={mdiOpenInNew} size={1.2} className="app-training-cta-icon" />
          )}
          <span>Open</span>
        </button>
      ) : null}
    </section>
  )
}

function DetailSection({ label, value }: { label: string; value: string }) {
  return (
    <div className="training-detail-section">
      <div className="training-detail-heading">{label}</div>
      <p className="training-detail-value">{value}</p>
    </div>
  )
}
